import React, { useState, useReducer, useEffect } from 'react';
import { useHistory } from '../../../hooks/useRouterCompat';
import { t, Trans } from '@lingui/macro';
import { Plus, X, Trash2, Mail, Globe, Settings, Save } from 'lucide-react';
import { Workspace } from '../../../models/Workspace';
import { Fire } from '../../../services/FireClient';
import { UtilApp } from '../../../services/Utils/UtilApp';
import { My } from '../../../services/My';
import { pojo } from '../../../services/Utils/Util';
import { useToast } from '../../../components/ui/use-toast';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../../components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../../components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "../../../components/ui/alert-dialog";
import WSOpenAIKey from './WSOpenAIKey';

interface WSAdminSettingsProps {
    workspace: Workspace;
    onUpdate?: (silent?: boolean) => void;
}

const WSAdminSettings: React.FC<WSAdminSettingsProps> = ({ workspace: initialWorkspace, onUpdate }) => {
    const [workspace, setWorkspace] = useState<Workspace>(() => pojo(initialWorkspace) as Workspace);
    const [inProgress, setInProgress] = useState(false);
    const { toast } = useToast();
    const history = useHistory();
    const [_, forceUpdate] = useReducer((x) => x + 1, 0);

    // Domain Modal State
    const [isDomainModalOpen, setIsDomainModalOpen] = useState(false);
    const [domainName, setDomainName] = useState("");
    const [autoJoin, setAutoJoin] = useState(false);

    useEffect(() => {
        // Sync with prop if it changes significantly, but usually we want to edit local copy
        if (initialWorkspace.id !== workspace.id) {
            setWorkspace(pojo(initialWorkspace) as Workspace);
        }
    }, [initialWorkspace.id]);

    const handleSave = async () => {
        if (!workspace) return;
        console.log('[WSAdminSettings] handleSave started', { id: workspace.id, name: workspace.name });
        setInProgress(true);
        try {
            // 10초 타임아웃 설정
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Save timeout after 10 seconds')), 10000)
            );

            console.log('[WSAdminSettings] Calling updateWorkspace...');
            await Promise.race([
                Fire.ws.updateWorkspace(workspace),
                timeoutPromise
            ]);
            console.log('[WSAdminSettings] updateWorkspace completed successfully');

            toast({ title: t`Workspace updated`, duration: 2000 });
            onUpdate?.(true);
        } catch (e) {
            console.error('[WSAdminSettings] Failed to update workspace:', e);
            toast({ title: t`Failed to update workspace` });
        } finally {
            console.log('[WSAdminSettings] handleSave finished, resetting inProgress');
            setInProgress(false);
        }
    };



    const deleteWorkspace = async () => {
        if (!workspace?.id) return;

        if (workspace.tier !== 'FREE' && workspace.tier !== 'PREMIUM_TRIAL' && workspace.tier) {
            toast({
                title: t`Cannot delete workspace. You must cancel your subscription first.`
            });
            // Optionally redirect to subscription page
            // history.push(`/ws-admin/subscription/${workspace.id}`);
            return;
        }

        setInProgress(true);
        try {
            await Fire.ws.deleteWorkspace(workspace.id);
            history.replace('/');
        } catch (e) {
            toast({ title: t`Failed to delete workspace` });
            setInProgress(false);
        }
    };

    // Domain Logic
    const deleteDomain = (domain: string) => {
        const index = workspace.emailDomains.indexOf(domain);
        if (index > -1) {
            workspace.emailDomains.splice(index, 1);
            // Also remove from autoJoin if present
            const autoIndex = workspace.autoJoinEmailDomains.indexOf(domain);
            if (autoIndex > -1) {
                workspace.autoJoinEmailDomains.splice(autoIndex, 1);
            }
            forceUpdate();
        }
    };

    const isValidEmailDomain = (domain: string) => {
        const re = new RegExp('^[a-z0-9]+([\\-\\.]{1}[a-z0-9]+)*\\.[a-z]{2,5}$');
        return re.test(domain);
    };

    const addEmailDomain = () => {
        if (domainName.trim() === '') {
            toast({ title: t`Please enter a valid domain` });
            return;
        }
        const domain = domainName.trim().toLowerCase().replace('@', '');

        if (workspace.emailDomains.includes(domain)) {
            toast({ title: t`Domain is already added` });
            return;
        }

        if (!isValidEmailDomain(domain)) {
            toast({ title: t`Invalid domain format` });
            return;
        }

        workspace.emailDomains.push(domain);
        if (autoJoin) {
            workspace.autoJoinEmailDomains.push(domain);
        }

        setIsDomainModalOpen(false);
        setDomainName("");
        setAutoJoin(false);
        forceUpdate();
    };

    return (
        <div className="w-full max-w-7xl p-4 md:p-6 lg:p-8 space-y-8 bg-white my-6 mx-4 rounded-panel shadow-sm border">
            <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-slate-100 border border-slate-200">
                    <Settings className="h-6 w-6 text-slate-600" />
                </div>
                <div>
                    <h2 className="text-2xl font-bold tracking-tight"><Trans>Workspace Settings</Trans></h2>
                    <p className="text-sm text-muted-foreground"><Trans>Manage your workspace profile and preferences.</Trans></p>
                </div>
            </div>

            {/* General Profile */}
            <Card>
                <CardHeader>
                    <CardTitle><Trans>General Information</Trans></CardTitle>
                    <CardDescription><Trans>Visible to all members of the workspace.</Trans></CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="ws-name"><Trans>Workspace Name</Trans></Label>
                        <Input
                            id="ws-name"
                            value={workspace.name}
                            onChange={e => setWorkspace({ ...workspace, name: e.target.value })}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="ws-desc"><Trans>Description</Trans></Label>
                        <Textarea
                            id="ws-desc"
                            value={workspace.description}
                            onChange={e => setWorkspace({ ...workspace, description: e.target.value })}
                            placeholder={t`No description`}
                            className="resize-none min-h-[100px]"
                            maxLength={400}
                        />
                    </div>
                </CardContent>
                <CardFooter className="border-t px-6 py-4 bg-gray-50/50 flex justify-end">
                    <Button onClick={handleSave} disabled={inProgress}>
                        {inProgress ? <Trans>Saving...</Trans> : <><Save className="w-4 h-4 mr-2" /><Trans>Save Changes</Trans></>}
                    </Button>
                </CardFooter>
            </Card>

            {/* Access Control */}
            <Card>
                <CardHeader>
                    <CardTitle><Trans>Access & Permissions</Trans></CardTitle>
                    <CardDescription><Trans>Control how users can join your workspace.</Trans></CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">

                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <Label className="text-base font-medium"><Trans>Allowed Email Domains</Trans></Label>
                            <Dialog open={isDomainModalOpen} onOpenChange={setIsDomainModalOpen}>
                                <DialogTrigger asChild>
                                    <Button variant="outline" size="sm">
                                        <Plus className="w-4 h-4 mr-2" /> <Trans>Add Domain</Trans>
                                    </Button>
                                </DialogTrigger>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle><Trans>Add Email Domain</Trans></DialogTitle>
                                        <DialogDescription><Trans>Allow users with specific email domains to join.</Trans></DialogDescription>
                                    </DialogHeader>
                                    <div className="grid gap-4 py-4">
                                        <div className="grid gap-2">
                                            <Label htmlFor="domain"><Trans>Domain</Trans></Label>
                                            <Input
                                                id="domain"
                                                placeholder="example.com"
                                                value={domainName}
                                                onChange={e => setDomainName(e.target.value)}
                                            />
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <Switch
                                                id="auto-join"
                                                checked={autoJoin}
                                                onCheckedChange={setAutoJoin}
                                            />
                                            <Label htmlFor="auto-join"><Trans>Enable Auto-Join for this domain</Trans></Label>
                                        </div>
                                        <p className="text-xs text-muted-foreground"><Trans>Users with this domain will automatically join when they sign up.</Trans></p>
                                    </div>
                                    <DialogFooter>
                                        <Button variant="outline" onClick={() => setIsDomainModalOpen(false)}><Trans>Cancel</Trans></Button>
                                        <Button onClick={addEmailDomain}><Trans>Add</Trans></Button>
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>
                        </div>

                        {workspace.emailDomains.length === 0 ? (
                            <div className="text-sm text-muted-foreground bg-gray-50 p-3 rounded-md border border-dashed text-center">
                                <Trans>Any domain is allowed (No restrictions)</Trans>
                            </div>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {workspace.emailDomains.map(domain => {
                                    const isAutoJoin = workspace.autoJoinEmailDomains.includes(domain);
                                    return (
                                        <Badge key={domain} variant="secondary" className="pl-2 pr-1 py-1 flex items-center gap-1">
                                            {isAutoJoin ? <Mail className="w-3 h-3 text-primary mr-1" /> : <Globe className="w-3 h-3 text-gray-400 mr-1" />}
                                            {domain}
                                            <Button variant="ghost" size="icon" className="h-4 w-4 ml-1 hover:bg-red-100 hover:text-red-500 rounded-full" onClick={() => deleteDomain(domain)}>
                                                <X className="w-3 h-3" />
                                            </Button>
                                        </Badge>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label><Trans>Invitation Policy</Trans></Label>
                        <Select
                            value={workspace.invitationPolicy}
                            onValueChange={(val: any) => setWorkspace({ ...workspace, invitationPolicy: val })}
                        >
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="ALLOW_ANY_EMAIL_DOMAIN"><Trans>Any user can join via invitation</Trans></SelectItem>
                                <SelectItem value="ALLOW_LISTED_EMAIL_DOMAIN"><Trans>Require allowed email domain</Trans></SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                </CardContent>
                <CardFooter className="border-t px-6 py-4 bg-gray-50/50 flex justify-end">
                    <Button onClick={handleSave} disabled={inProgress}>
                        {inProgress ? <Trans>Saving...</Trans> : <><Save className="w-4 h-4 mr-2" /><Trans>Save Changes</Trans></>}
                    </Button>
                </CardFooter>
            </Card>

            {/* OpenAI Key (Conditional) */}
            {(workspace.id === "odo_bang" || workspace.id === "jiranfamaily" || workspace.id === "test") && (
                <Card>
                    <CardHeader>
                        <CardTitle><Trans>Integrations</Trans></CardTitle>
                    </CardHeader>
                    <CardContent>
                        <WSOpenAIKey workspaceId={workspace.id} />
                    </CardContent>
                </Card>
            )}

            {/* Danger Zone */}
            <Card className="border-red-200">
                <CardHeader>
                    <CardTitle className="text-red-600"><Trans>Danger Zone</Trans></CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center justify-between">
                        <div className="space-y-1">
                            <h4 className="font-medium"><Trans>Delete this workspace</Trans></h4>
                            <p className="text-sm text-muted-foreground"><Trans>Once you delete a workspace, there is no going back. Please be certain.</Trans></p>
                        </div>

                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <Button variant="destructive"><Trash2 className="w-4 h-4 mr-2" /><Trans>Delete Workspace</Trans></Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle><Trans>Are you absolutely sure?</Trans></AlertDialogTitle>
                                    <AlertDialogDescription>
                                        <Trans>This action cannot be undone. This will permanently delete the workspace <strong>{workspace.name}</strong> and remove all associated data.</Trans>
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
                                    <AlertDialogAction onClick={deleteWorkspace} className="bg-red-600 hover:bg-red-700"><Trans>Delete</Trans></AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </div>
                </CardContent>
                <CardFooter className="border-t bg-red-50/50 px-6 py-4 flex justify-between items-center text-xs text-muted-foreground">
                    <span>
                        <Trans>{workspace.name} was created {UtilApp.dateStrToDay(workspace.created, My.user.lang)} and is managed by {workspace.owner.name}</Trans>
                    </span>
                </CardFooter>
            </Card>

        </div>
    );
};

export default WSAdminSettings;
